import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { fmtDate, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Check, Sparkles, Trash2, Loader2 } from "lucide-react";

function ActionRow({ action, planId, onToggled }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/plans/${planId}/actions/${action.id}`,
        { done: !action.done },
      );
      onToggled(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`card-surface p-5 flex gap-4 transition-opacity ${action.done ? "opacity-60" : ""}`} data-testid={`plan-action-${action.id}`}>
      <button
        onClick={toggle}
        disabled={busy}
        data-testid={`toggle-action-${action.id}`}
        aria-pressed={action.done}
        className={`w-6 h-6 mt-0.5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
          action.done ? "bg-forest border-forest" : "border-ink-lighter/40 hover:border-forest"
        }`}
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin text-white" /> : action.done && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
      </button>
      <div className="flex-1">
        <div className={`font-medium text-ink mb-1 ${action.done ? "line-through" : ""}`} data-testid={`action-title-${action.id}`}>
          {action.title}
        </div>
        {action.detail && (
          <p className="text-sm text-ink-light leading-relaxed">{action.detail}</p>
        )}
        {action.impact_estimate && (
          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-forest/8 text-forest text-xs font-medium num">
            {action.impact_estimate}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PlanDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/plans/${id}`)
      .then((r) => setPlan(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [id]);

  const remove = async () => {
    if (!window.confirm("Delete this plan?")) return;
    try {
      await api.delete(`/plans/${id}`);
      toast.success("Plan deleted");
      nav("/app");
    } catch (e) { toast.error("Delete failed"); }
  };

  if (loading) return <div className="p-8 text-ink-lighter">Loading…</div>;
  if (!plan) return <div className="p-8 text-destructive">Plan not found.</div>;

  const done = plan.actions.filter((a) => a.done).length;
  const pct = plan.actions.length === 0 ? 0 : Math.round((done / plan.actions.length) * 100);

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid="plan-page">
      <Link to="/app/advisor" data-testid="plan-back" className="text-xs text-ink-light hover:text-ink inline-flex items-center gap-1.5 mb-6">
        <ArrowLeft className="w-3 h-3" /> Back to AI Advisor
      </Link>

      <div className="overline mb-2 inline-flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" strokeWidth={2} /> 90-day plan
      </div>
      <h1 className="font-heading text-4xl font-medium tracking-tight" data-testid="plan-title">{plan.title}</h1>
      {plan.source_question && (
        <p className="text-ink-light mt-3 leading-relaxed">
          <span className="text-ink-lighter text-xs uppercase tracking-wider mr-2">From your question</span>
          “{plan.source_question}”
        </p>
      )}

      <div className="card-surface p-5 my-7 flex items-center justify-between gap-4" data-testid="plan-progress">
        <div className="flex-1">
          <div className="flex items-baseline justify-between mb-2">
            <div className="overline">Progress</div>
            <div className="text-xs text-ink-light">
              <span className="num font-medium text-ink">{done}</span> of <span className="num">{plan.actions.length}</span> done · <span className="num">{pct}%</span>
            </div>
          </div>
          <div className="h-2 rounded-full bg-paper overflow-hidden">
            <div
              className="h-full bg-forest transition-all duration-300"
              style={{ width: `${pct}%` }}
              data-testid="progress-bar"
            />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {plan.actions.map((a) => (
          <ActionRow key={a.id} action={a} planId={plan.id}
            onToggled={(updated) => setPlan(updated)} />
        ))}
      </div>

      <div className="mt-10 pt-6 border-t border-border flex items-center justify-between text-xs text-ink-lighter">
        <span>Created {fmtDate(plan.created_at)}</span>
        <button onClick={remove} className="hover:text-destructive inline-flex items-center gap-1.5" data-testid="plan-delete">
          <Trash2 className="w-3.5 h-3.5" /> Delete plan
        </button>
      </div>
    </div>
  );
}
